import React from 'react';
import { motion } from 'framer-motion';
import { Quote, Sparkles, ShieldCheck, Globe, Crown } from 'lucide-react';

/**
 * PremiumAbout — Editorial "About BITZ Club International" section.
 *
 * Tone: international premium standard, lifestyle + smart everyday value,
 * scalable/future-ready (no fake global claims).
 *
 * Layout:
 *   - Eyebrow + editorial headline + supporting paragraph
 *   - Two-column split:  copy (left)  |  image collage (right)
 *   - Pull-quote band
 *   - Brand-pillars row (3 cards positioning the ecosystem)
 */

const PILLARS = [
  {
    icon: Crown,
    title: 'Aspirational Access',
    body:
      'A growing network of affiliated hotels, resorts, dining, wellness and lifestyle partners — opened up through one membership.',
  },
  {
    icon: Sparkles,
    title: 'Everyday Smart Value',
    body:
      'A trusted daily-needs ecosystem of vetted partners — from home services to provisions — organised around members, not advertisers.',
  },
  {
    icon: Globe,
    title: 'Built to Scale Globally',
    body:
      'A partner-first model designed to international standards today, ready to add new affiliations in new cities and countries tomorrow.',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PremiumAbout = () => {
  return (
    <section
      id="about"
      className="relative py-20 sm:py-28 lg:py-32 bg-[#0A0A0B] overflow-hidden"
      data-testid="premium-about"
    >
      {/* Ambient gold corner glow */}
      <div
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.07] pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #D4AF37 0%, transparent 60%)',
        }}
      />
      <div
        className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full opacity-[0.05] pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #0D1B3F 0%, transparent 60%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* Eyebrow + headline */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
          className="max-w-3xl mb-14 lg:mb-20"
        >
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span
              className="text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#D4AF37] font-medium"
              style={{ fontFamily: 'Cinzel, Playfair Display, serif' }}
            >
              About BITZ Club International
            </span>
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6"
            style={{ fontFamily: 'Playfair Display, Cinzel, serif' }}
          >
            A members&apos; network for those who want their{' '}
            <span className="bg-gradient-to-r from-[#F5D77A] via-[#D4AF37] to-[#A67C00] bg-clip-text text-transparent">
              everyday to feel premium.
            </span>
          </h2>
          <p
            className="text-base sm:text-lg text-gray-300/85 leading-relaxed max-w-2xl"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            BITZ Club International (BCI) is a premium lifestyle access club. We don&apos;t
            run the hotels, restaurants or spas — we build the network that brings the
            best of them to our members. Two worlds, one elegant membership.
          </p>
        </motion.div>

        {/* Editorial split: copy | collage */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center mb-20 lg:mb-28">
          {/* LEFT — copy */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
            variants={fadeUp}
            className="lg:col-span-6 space-y-6"
          >
            <p className="text-gray-200/90 text-base sm:text-lg leading-relaxed">
              On one side, a carefully curated <strong className="text-[#D4AF37] font-semibold">network
              of affiliated hotels, resorts, restaurants, spas and clubs</strong> —
              partners we&apos;d personally recommend to a close friend. Members enjoy
              meaningful privileges, priority access and the kind of welcome you don&apos;t
              get from a generic booking app.
            </p>

            <p className="text-gray-200/90 text-base sm:text-lg leading-relaxed">
              On the other side, a quietly powerful <strong className="text-[#D4AF37] font-semibold">daily-needs
              ecosystem</strong> of trusted partners — home services, wellness, provisions
              and verified vendors — organised around members, not advertisers. No
              clutter, no race-to-the-bottom coupons. Just a reliable, well-run network
              you can depend on.
            </p>

            <p className="text-gray-200/90 text-base sm:text-lg leading-relaxed">
              The result is access to a growing world of premium experiences and reliable
              everyday support — built today around our network in India, designed to
              international standards, and ready to add new affiliations as our members
              travel the world.
            </p>

            {/* Stats strip — partner / network framing */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4 pt-6">
              {[
                { v: '20+', l: 'Years of\nhospitality' },
                { v: '600+', l: 'Affiliated\npartner venues' },
                { v: 'One', l: 'Membership.\nA whole network.' },
              ].map((s) => (
                <div
                  key={s.l}
                  className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 sm:p-5"
                >
                  <p
                    className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#D4AF37] mb-1"
                    style={{ fontFamily: 'Playfair Display, Cinzel, serif' }}
                  >
                    {s.v}
                  </p>
                  <p className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-gray-400 leading-snug whitespace-pre-line">
                    {s.l}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT — Image collage */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
            variants={fadeUp}
            className="lg:col-span-6"
          >
            <div className="relative grid grid-cols-6 grid-rows-6 gap-3 sm:gap-4 aspect-[5/6] sm:aspect-[6/5] lg:aspect-[5/6]">
              {/* Large signature frame */}
              <div className="col-span-6 row-span-4 rounded-2xl overflow-hidden group">
                <img
                  src="https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1400&q=80&auto=format&fit=crop"
                  alt="Affiliated hospitality"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                  loading="lazy"
                />
              </div>
              {/* Bottom-left smaller */}
              <div className="col-span-3 row-span-2 rounded-2xl overflow-hidden group">
                <img
                  src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=900&q=80&auto=format&fit=crop"
                  alt="Partner dining experiences"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                  loading="lazy"
                />
              </div>
              {/* Bottom-right smaller */}
              <div className="col-span-3 row-span-2 rounded-2xl overflow-hidden group relative">
                <img
                  src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=900&q=80&auto=format&fit=crop"
                  alt="Wellness affiliations"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                  loading="lazy"
                />
              </div>

              {/* Floating credential badge */}
              <div className="absolute -bottom-5 -left-5 hidden sm:flex bg-[#0F0F10] border border-[#D4AF37]/30 rounded-2xl px-5 py-4 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p
                    className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-medium"
                    style={{ fontFamily: 'Cinzel, serif' }}
                  >
                    Curated Affiliations
                  </p>
                  <p className="text-sm text-white font-medium">Verified · Trusted · Member-first</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Pull-quote band */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-60px' }}
          variants={fadeUp}
          className="relative max-w-4xl mx-auto mb-20 lg:mb-28"
        >
          <div className="relative bg-gradient-to-br from-[#101012] to-[#1A1A1C] border border-[#D4AF37]/15 rounded-3xl px-8 sm:px-12 py-10 sm:py-14 text-center overflow-hidden">
            <div
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse at center, rgba(212,175,55,0.15), transparent 65%)',
              }}
            />
            <Quote className="w-8 h-8 sm:w-10 sm:h-10 text-[#D4AF37] mx-auto mb-5 opacity-70" />
            <p
              className="relative text-xl sm:text-2xl lg:text-3xl text-white leading-snug font-medium italic mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              &ldquo;The best clubs don&apos;t own everything. They open every door.&rdquo;
            </p>
            <p
              className="relative text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#D4AF37]"
              style={{ fontFamily: 'Cinzel, serif' }}
            >
              The BITZ Philosophy
            </p>
          </div>
        </motion.div>

        {/* Three brand pillars */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {PILLARS.map((p, i) => {
            const Ico = p.icon;
            return (
              <motion.div
                key={p.title}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-40px' }}
                custom={i}
                variants={fadeUp}
                className="group relative bg-gradient-to-br from-[#101012] to-[#15151A] border border-white/5 hover:border-[#D4AF37]/40 rounded-2xl p-7 sm:p-8 transition-all duration-500 hover:translate-y-[-4px] hover:shadow-[0_24px_60px_-20px_rgba(212,175,55,0.18)]"
                data-testid={`about-pillar-${i}`}
              >
                <div
                  className="absolute -top-12 -right-12 w-44 h-44 rounded-full opacity-0 group-hover:opacity-15 blur-3xl transition-opacity duration-700 pointer-events-none"
                  style={{ background: '#D4AF37' }}
                />
                <div className="relative">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-5">
                    <Ico className="w-6 h-6 sm:w-7 sm:h-7 text-[#D4AF37]" />
                  </div>
                  <h3
                    className="text-xl sm:text-2xl font-bold text-white mb-3"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {p.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-400 leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PremiumAbout;
